import { Operator } from '../../types/enums';
import { getByPath } from '../interpolation/getByPath';
import type { PathSegment } from '../interpolation/types';
import {
  rootForSource,
  type RootSourceContext,
} from '../binding-resolver/rootForSource';
import { newRelicNoticeError } from '@de/utils/newRelicActions';

/**
 * Runtime context handed to the visibility predicate. Structurally the
 * same root shape the binding resolver uses ({@link RootSourceContext}),
 * so `when.source` (`item`/`formState`/`context`/`<dataSource>`) resolves
 * to exactly the value a sibling `_bind.*` on the same node would see.
 */
export type VisibilityContext = RootSourceContext;

/**
 * Value predicate carried under `_meta.visibility.when`.
 *
 * Evaluated against resolved bind data — inside a
 * `platform.dataRepeater` it resolves per row against `source: 'item'`.
 * `operator` mirrors the dynamic-flows `Condition` vocabulary (the
 * runtime reads `operator`, not `op`) so flow branching and node
 * visibility share one operator set.
 */
export interface VisibilityWhen {
  readonly source: string;
  readonly path: string;
  readonly operator: Operator;
  readonly value?: unknown;
}

/**
 * hen-ui–aligned breakpoint tokens for `_meta.visibility.showOn`.
 * Matches admin + dynamic-flows allow-lists (`mobile` / `tablet` /
 * `desktop`).
 */
export const VISIBILITY_BREAKPOINT_MEDIA = {
  mobile: '(max-width: 767px)',
  tablet: '(min-width: 768px) and (max-width: 1023px)',
  desktop: '(min-width: 1024px)',
} as const;

export type VisibilityBreakpointToken =
  keyof typeof VISIBILITY_BREAKPOINT_MEDIA;

/**
 * The `_meta.visibility` bag. `when` (value predicate) and `showOn`
 * (breakpoint predicate) are ANDed; either may be omitted.
 *
 * `showOn` is evaluated against the active viewport tokens from
 * {@link evaluateShowOn}. Empty / missing `showOn` →
 * visible. `mode: 'css'` keeps the node mounted but visually hidden
 * off-breakpoint (see the binding-enhancer `VisibilityGate`); value
 * predicates (`when`) remain mount-only.
 *
 * Trackable still mounts under `mode: 'css'` hide — prefer
 * `mode: 'mount'` for tracked nodes when phantom views must not fire.
 */
export interface VisibilityMeta {
  readonly when?: VisibilityWhen;
  readonly showOn?: readonly string[];
  readonly mode?: 'mount' | 'css';
}

const VALID_OPERATORS: ReadonlySet<string> = new Set<string>(
  Object.values(Operator),
);

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * Turn a plain dot-path (`"card.kind"`) into the pre-parsed segment
 * array {@link getByPath} consumes. Wildcards are not part of the
 * value-predicate grammar (a predicate compares one resolved value), so
 * every segment is a plain key. Empty / blank segments are dropped.
 */
function pathToSegments(path: string): ReadonlyArray<PathSegment> {
  return path
    .split('.')
    .map((name) => name.trim())
    .filter((name) => name.length > 0)
    .map((name) => ({ type: 'key' as const, name }));
}

/**
 * `true` when `resolved` contains `value`:
 *   - array `resolved` → some entry matches `value` under `String(...)`
 *     coercion (robust to number/string mixes in `item.kind`-style data).
 *   - otherwise → `String(resolved)` includes the `String(value)`
 *     substring.
 */
function containsValue(resolved: unknown, value: unknown): boolean {
  if (Array.isArray(resolved)) {
    return resolved.some((entry) => String(entry) === String(value));
  }
  return String(resolved).includes(String(value));
}

/** True when `resolved` contains ANY member of array `value`; a non-array `value` matches nothing. */
function containsAny(resolved: unknown, value: unknown): boolean {
  return (
    Array.isArray(value) &&
    value.some((member) => containsValue(resolved, member))
  );
}

/**
 * Compare a resolved value against the predicate `value` per `operator`.
 *
 * Coercion (parity with dynamic-flows Condition operators):
 *   - `EQUALS` / `NOT_EQUALS` / `CONTAINS` → `String(...)` coercion.
 *   - `CONTAINS_ANY` → array `value`; true if ANY member matches /
 *     is contained.
 *   - `NOT_CONTAINS_ANY` → array `value`; the negation of the above.
 *   - `GREATER_THAN` / `LESS_THAN` → numeric (`Number(...)`); a `NaN`
 *     coercion naturally yields `false` (fail-closed).
 *
 * An unresolved value (`undefined` / `null`) is a clean non-match for every
 * positive operator (fail-closed: a `when` that cannot resolve hides the node).
 * `NOT_CONTAINS_ANY` is the exception — a negative membership test is vacuously
 * true when the value is absent, which is what lets it express a default ("show
 * undergrad unless graduate"). A non-array `value` is likewise an empty
 * exclusion list, so it too yields `true`.
 */
function compare(
  operator: Operator,
  resolved: unknown,
  value: unknown,
): boolean {
  if (resolved === undefined || resolved === null) {
    return operator === Operator.NOT_CONTAINS_ANY;
  }
  switch (operator) {
    case Operator.EQUALS:
      return String(resolved) === String(value);
    case Operator.NOT_EQUALS:
      return String(resolved) !== String(value);
    case Operator.CONTAINS:
      return containsValue(resolved, value);
    case Operator.CONTAINS_ANY:
      return containsAny(resolved, value);
    case Operator.NOT_CONTAINS_ANY:
      return !containsAny(resolved, value);
    case Operator.GREATER_THAN:
      return Number(resolved) > Number(value);
    case Operator.LESS_THAN:
      return Number(resolved) < Number(value);
    default:
      return false;
  }
}

/**
 * Result of {@link parseVisibilityWhen}: a typed {@link VisibilityWhen}
 * or a structured reason (never throws). The reason string is namespace
 * -free (`when.source must be …`) so callers can prefix it with their own
 * context (`_meta.visibility.` vs `_bind.*.keys[].`).
 */
export type ParseVisibilityWhenResult =
  | { readonly ok: true; readonly when: VisibilityWhen }
  | { readonly ok: false; readonly reason: string };

/**
 * Validate a raw value predicate into a typed {@link VisibilityWhen}.
 *
 * Shared by the `_meta.visibility` gate ({@link parseVisibilityMeta}) and
 * the per-binding `_bind.*.keys[].when` filter so both surfaces enforce an
 * identical predicate shape. Trims `source` / `path` and rejects
 * whitespace-only values (a blank predicate is malformed, not a clean
 * non-match) and unknown operators. Never throws.
 */
export function parseVisibilityWhen(raw: unknown): ParseVisibilityWhenResult {
  if (!isPlainObject(raw)) {
    return { ok: false, reason: 'when must be an object' };
  }
  const { source, path, operator, value } = raw as {
    source?: unknown;
    path?: unknown;
    operator?: unknown;
    value?: unknown;
  };
  const trimmedSource = typeof source === 'string' ? source.trim() : '';
  if (trimmedSource.length === 0) {
    return { ok: false, reason: 'when.source must be a non-empty string' };
  }
  const trimmedPath = typeof path === 'string' ? path.trim() : '';
  if (trimmedPath.length === 0) {
    return { ok: false, reason: 'when.path must be a non-empty string' };
  }
  if (typeof operator !== 'string' || !VALID_OPERATORS.has(operator)) {
    return { ok: false, reason: 'when.operator must be a known Operator' };
  }
  return {
    ok: true,
    when: {
      source: trimmedSource,
      path: trimmedPath,
      operator: operator as Operator,
      value,
    },
  };
}

/**
 * Resolve `when.source` + `when.path` against `ctx` and compare per
 * `when.operator`. Pure and total — never throws.
 */
export function evaluateVisibilityWhen(
  when: VisibilityWhen,
  ctx: VisibilityContext,
): boolean {
  const root = rootForSource(when.source, ctx);
  const segments = pathToSegments(when.path);
  const resolved =
    segments.length === 0 ? undefined : getByPath(root, segments);
  return compare(when.operator, resolved, when.value);
}

/**
 * Breakpoint half of the shared visibility gate.
 *
 * Pure — callers resolve the active viewport via
 * `useActiveVisibilityBreakpoints` (or a test double) and pass the
 * matching token set. Semantics:
 *   - empty / missing `showOn` → visible
 *   - OR across listed tokens (any active match → visible)
 *   - unknown tokens never match (fail closed for that entry; other
 *     listed tokens still OR)
 */
export function evaluateShowOn(
  showOn: readonly string[] | undefined,
  activeMatches: ReadonlySet<string>,
): boolean {
  if (showOn === undefined || showOn.length === 0) {
    return true;
  }
  return showOn.some((token) => activeMatches.has(token));
}

/**
 * The composed visibility decision: `when` AND `showOn`. Pure.
 *
 * Note: `mode: 'css'` is honored by the render-path gate (keep mounted
 * but hide), not by this boolean — a `false` here means "not visible
 * for mount purposes". Pass the active breakpoint set from the viewport
 * hook (empty set = no breakpoint matches).
 */
export function isNodeVisible(
  visibility: VisibilityMeta,
  ctx: VisibilityContext,
  activeMatches: ReadonlySet<string> = new Set(),
): boolean {
  const whenResult =
    visibility.when === undefined
      ? true
      : evaluateVisibilityWhen(visibility.when, ctx);
  const showOnResult = evaluateShowOn(visibility.showOn, activeMatches);
  return whenResult && showOnResult;
}

type VisibilityParseResult =
  | { readonly ok: true; readonly meta: VisibilityMeta }
  | { readonly ok: false; readonly reason: string };

/**
 * Validate a raw `_meta.visibility` value into a typed
 * {@link VisibilityMeta}. Returns a structured `ok: false` (never
 * throws) for a structurally malformed shape so the gate can degrade
 * gracefully (treat as visible + notice) rather than blank a page.
 */
export function parseVisibilityMeta(raw: unknown): VisibilityParseResult {
  if (!isPlainObject(raw)) {
    return { ok: false, reason: '_meta.visibility must be a plain object' };
  }

  let when: VisibilityWhen | undefined;
  if (raw.when !== undefined) {
    // Delegate to the shared predicate parser so `_meta.visibility.when`
    // and `_bind.*.keys[].when` enforce an identical shape (trimmed,
    // non-empty `source`/`path`; known `operator`). Prefix the namespace
    // -free reason so the malformed → visible + notice posture keeps its
    // original message surface.
    const parsedWhen = parseVisibilityWhen(raw.when);
    // Use `=== false` (not `!parsedWhen.ok`): WP Engine's headless build
    // runs an older TS that only narrows this discriminated union through
    // an explicit literal comparison, not the `!` negation operator.
    if (parsedWhen.ok === false) {
      return { ok: false, reason: `_meta.visibility.${parsedWhen.reason}` };
    }
    when = parsedWhen.when;
  }

  let showOn: readonly string[] | undefined;
  if (raw.showOn !== undefined) {
    if (
      !Array.isArray(raw.showOn) ||
      !raw.showOn.every((entry) => typeof entry === 'string')
    ) {
      return {
        ok: false,
        reason: '_meta.visibility.showOn must be an array of strings',
      };
    }
    showOn = raw.showOn as readonly string[];
  }

  let mode: 'mount' | 'css' | undefined;
  if (raw.mode !== undefined) {
    if (raw.mode !== 'mount' && raw.mode !== 'css') {
      return {
        ok: false,
        reason: '_meta.visibility.mode must be "mount" or "css"',
      };
    }
    mode = raw.mode;
  }

  return { ok: true, meta: { when, showOn, mode } };
}

/**
 * Decide whether a node carrying a raw `_meta.visibility` value should
 * mount. The single entry point the render-path gate calls.
 *
 * Posture (confirmed defaults):
 *   - A clean non-match (predicate evaluates `false`, incl. unresolved
 *     value) → hide (`false`, fail-closed).
 *   - A structurally MALFORMED `_meta.visibility` → treat as VISIBLE
 *     (`true`, fail-open) and report through `newRelicNoticeError` so a
 *     bad config degrades gracefully instead of blanking a page. Never
 *     throws.
 */
export function isNodeVisibleFromRaw(
  raw: unknown,
  ctx: VisibilityContext,
  activeMatches: ReadonlySet<string> = new Set(),
): boolean {
  const parsed = parseVisibilityMeta(raw);
  if (parsed.ok === false) {
    newRelicNoticeError(
      new Error(
        `[binding-enhancer] Malformed _meta.visibility: ${parsed.reason}`,
      ),
      { source: 'binding-enhancer:visibility' },
    );
    return true;
  }
  return isNodeVisible(parsed.meta, ctx, activeMatches);
}
